import decodePolyline from "polyline";

function safeRequestId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function xmlEscape(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");
}

function sanitizeFilePart(value) {
  return String(value || "activity-route")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "activity-route";
}

function safeDecode(polyline) {
  if (!polyline || typeof polyline !== "string") return [];
  try {
    const decoded = decodePolyline.decode(polyline);
    return decoded.filter(([lat, lon]) => (
      Number.isFinite(lat) && Number.isFinite(lon)
      && lat >= -90 && lat <= 90
      && lon >= -180 && lon <= 180
    ));
  } catch {
    return [];
  }
}

export function createGpxPayload({
  polyline,
  activityName,
  activityId,
  activityDate,
}) {
  const points = safeDecode(polyline);
  if (points.length < 2) return null;

  const routeName = activityName || "Activity Route";
  const fileStem = sanitizeFilePart(`${activityDate || "undated"}-${routeName}`);
  const filename = `${fileStem}.gpx`;

  const trkPoints = points
    .map(([lat, lon]) => `      <trkpt lat="${Number(lat).toFixed(6)}" lon="${Number(lon).toFixed(6)}" />`)
    .join("\n");

  const idTag = activityId == null
    ? ""
    : `<extensions><club:activityId>${xmlEscape(activityId)}</club:activityId></extensions>`;

  const content = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="club-analytics" xmlns="http://www.topografix.com/GPX/1/1" xmlns:club="https://clubspace.app/gpx/extensions">
  <metadata>
    <name>${xmlEscape(routeName)}</name>
  </metadata>
  <trk>
    <name>${xmlEscape(routeName)}</name>
    ${idTag}
    <trkseg>
${trkPoints}
    </trkseg>
  </trk>
</gpx>`;

  return {
    filename,
    content,
    pointCount: points.length,
  };
}

export function downloadGpx(payload) {
  if (!payload?.content || !payload?.filename) return false;
  const blob = new Blob([payload.content], { type: "application/gpx+xml;charset=utf-8" });
  downloadBlob(blob, payload.filename);
  return true;
}

export function downloadGpxFromActivity({
  polyline,
  activityName,
  activityId,
  activityDate,
}) {
  const payload = createGpxPayload({ polyline, activityName, activityId, activityDate });
  if (!payload) return false;
  return downloadGpx(payload);
}

function parseAttachmentFilename(contentDisposition) {
  if (!contentDisposition || typeof contentDisposition !== "string") return null;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  const basicMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return basicMatch?.[1] || null;
}

function downloadBlob(blob, filename) {
  if (!blob || !filename) return false;

  const shareApiAvailable = typeof navigator !== "undefined"
    && typeof navigator.share === "function"
    && typeof navigator.canShare === "function";

  const isNativePlatform = (() => {
    try {
      return Boolean(globalThis.Capacitor?.isNativePlatform?.());
    } catch {
      return false;
    }
  })();

  const userAgent = String(globalThis.navigator?.userAgent || "");
  const isAndroidWebView = /\bwv\b/i.test(userAgent);

  if (shareApiAvailable && (isNativePlatform || isAndroidWebView)) {
    try {
      const file = new File([blob], filename, { type: blob.type || "application/octet-stream" });
      if (navigator.canShare({ files: [file] })) {
        void navigator.share({ files: [file], title: filename }).catch(() => {});
        return true;
      }
    } catch {
      // Fall back to browser download when native sharing is unavailable.
    }
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return true;
}

async function fetchBackendGpx(activityId, fidelity) {
  return fetchBackendRouteExport({ activityId, format: "gpx", fidelity });
}

async function readBackendError(response) {
  try {
    const body = await response.json();
    if (body?.error) return String(body.error);
  } catch {
    // Ignore invalid error payloads.
  }
  return "export_failed";
}

async function fetchBackendRouteExport({ activityId, format, fidelity }) {
  const params = new URLSearchParams({
    format,
  });
  if (fidelity) params.set("fidelity", fidelity);

  const response = await fetch(`/api/activities/${activityId}/route-export?${params.toString()}`, {
    credentials: "include",
    headers: {
      "X-Request-Id": safeRequestId(),
    },
  });

  if (!response.ok) {
    const errorCode = await readBackendError(response);
    const error = new Error(`GPX export failed with status ${response.status}`);
    error.status = response.status;
    error.errorCode = errorCode;
    throw error;
  }

  const filename = parseAttachmentFilename(response.headers.get("content-disposition"))
    || `activity-${activityId}-${format}${fidelity ? `-${fidelity}` : ""}.${format}`;
  const blob = await response.blob();
  return downloadBlob(blob, filename);
}

export async function downloadGpxFromActivityFidelity({
  polyline,
  activityName,
  activityId,
  activityDate,
  fidelity = "summary",
}) {
  const exportFidelity = fidelity === "enriched" ? "enriched" : "summary";

  if (exportFidelity === "summary") {
    return { success: downloadGpxFromActivity({ polyline, activityName, activityId, activityDate }), fallbackUsed: false };
  }

  if (activityId != null) {
    try {
      const success = await fetchBackendGpx(activityId, "enriched");
      return { success, fallbackUsed: false };
    } catch {
      // Fall through to local summary GPX fallback.
    }
  }

  const fallbackSuccess = downloadGpxFromActivity({ polyline, activityName, activityId, activityDate });
  return { success: fallbackSuccess, fallbackUsed: fallbackSuccess };
}

export async function downloadFitFromActivity({ activityId }) {
  if (activityId == null) return { success: false, errorCode: "fit_unavailable" };

  try {
    const success = await fetchBackendRouteExport({ activityId, format: "fit" });
    return { success, errorCode: null };
  } catch (error) {
    return {
      success: false,
      errorCode: error?.errorCode || "export_failed",
      status: error?.status,
    };
  }
}
